/**
 * Indicator settings-form SCHEMA builders — Phase 6a-6 commit 18 of the
 * decomposition (briefs/ARCHITECTURE_DECOMPOSITION_BLUEPRINT.md §6, A19 pure
 * subset, v2.16/R32). Template literals moved VERBATIM from
 * static/index.html — these are the eight plugins' canonical form schemas:
 * every default (`??` vs `||` semantics preserved exactly), the two
 * percentage display transforms (panic_flush ×100 .toFixed(3);
 * psychological_view Math.round(×100)), and the CSS field-class contracts
 * that the page-side collectIndicatorSettings reads back. The defaults
 * mirror the backend plugins' parameter expectations — drift here means
 * silently wrong indicator params.
 *
 * FROZEN SURFACE includes the MARKUP-CLASS CONTRACT: `.ism-color-trigger`,
 * `.ism-color-native`, `.dsc-color-dot`, and every per-plugin field class —
 * the page-side ism colour-popover and collector operate on these classes.
 * Renaming any class here silently breaks page wiring; the exact-string
 * tests pin them.
 *
 * PERMANENT PAGE CORE, by v2.16 ruling (NOT here): the ism colour-popover
 * suite (module state + the A19⇄A17 dscBuildSwatchGrid edge),
 * readEmaFormRows/renderEmaForm (DOM + recursive listener wiring),
 * collectIndicatorSettings (DOM read-side of this schema contract), and the
 * open/back/save orchestrators (state + modal DOM + POST + recompute).
 *
 * Zero context, zero callbacks, zero imports — pure (data) → HTML string.
 */

export const EMA_PALETTE = ['#4DA3E0', '#ff9800', '#8e24aa', '#43a047', '#e53935', '#3949ab', '#00897b', '#fb8c00'];

export function buildEmaLineRow(cfg: Record<string, any>, index: number): string {
  const period = cfg.period ?? 20;
  const color = cfg.color || '#4DA3E0';
  const width = cfg.width ?? 2;
  const style = cfg.style ?? 0;
  return `
        <div class="ema-line-row" data-index="${index}" style="border-left-color:${color};">
          <input type="number" class="ema-period" value="${period}" min="1" max="500" title="Period">
          <span class="ism-color-cell">
            <button type="button" class="ism-color-trigger" title="Color"><span class="dsc-color-dot" style="background:${color};"></span></button>
            <input type="color" class="ism-color-native ema-color" value="${color}" style="display:none;">
          </span>
          <select class="ema-width" title="Width">
            <option value="1" ${width == 1 ? 'selected' : ''}>1px</option>
            <option value="2" ${width == 2 ? 'selected' : ''}>2px</option>
            <option value="3" ${width == 3 ? 'selected' : ''}>3px</option>
            <option value="4" ${width == 4 ? 'selected' : ''}>4px</option>
            <option value="5" ${width == 5 ? 'selected' : ''}>5px</option>
          </select>
          <select class="ema-style" title="Style">
            <option value="0" ${style == 0 ? 'selected' : ''}>Solid</option>
            <option value="2" ${style == 2 ? 'selected' : ''}>Dashed</option>
            <option value="1" ${style == 1 ? 'selected' : ''}>Dotted</option>
          </select>
          <button type="button" class="ema-remove-btn" title="Remove line">🗑</button>
        </div>
      `;
}

export function ismColorField(labelText: string, className: string, value: string): string {
  const v = value || '#000000';
  return `
        <div class="ism-color-field">
          <span>${labelText}</span>
          <button type="button" class="ism-color-trigger" title="${labelText}"><span class="dsc-color-dot" style="background:${v};"></span></button>
          <input type="color" class="ism-color-native ${className}" value="${v}" style="display:none;">
        </div>
      `;
}

export function buildIndicatorSettingsHTML(plugin: string, settings: Record<string, any>): string {
  settings = settings || {};
  if (plugin === 'ema') {
    return `
          <div class="ema-lines-head"><span>Period</span><span>Color</span><span>Width</span><span>Style</span><span></span></div>
          <div class="ema-lines-list"></div>
          <button type="button" class="ema-add-btn">+ Add EMA</button>
        `;
  }
  if (plugin === 'session_ema_pivots') {
    return `
          <div class="ism-fields">
            <label>EMA Period <input type="number" class="sepp-ema-period" value="${settings.ema_period || 20}" min="2" max="200"></label>
            <label>Pivot Lookback <input type="number" class="sepp-pivot-lb" value="${settings.pivot_lb || 5}" min="1" max="50"></label>
            <label>Observation Minutes <input type="number" class="sepp-obs-minutes" value="${settings.obs_minutes || 170}" min="10" max="500"></label>
          </div>
        `;
  }
  if (plugin === 'liquidity_sweep') {
    return `
          <div class="ism-fields">
            <label>Swing Pivot Lookback <input type="number" class="ls-liq-pivot-lb" value="${settings.liq_pivot_lb ?? 5}" min="1" max="20"></label>
            <label>Max Active Levels <input type="number" class="ls-max-liq-levels" value="${settings.max_liq_levels ?? 6}" min="1" max="20"></label>
            <label>Pattern 1 Window <input type="number" class="ls-win1" value="${settings.win1 ?? 150}" min="1" max="500"></label>
            <label>Activation Window <input type="number" class="ls-p1-act-win" value="${settings.p1_act_win ?? 150}" min="1" max="500"></label>
            <label>Pattern 2 Window <input type="number" class="ls-win2" value="${settings.win2 ?? 30}" min="1" max="500"></label>
            <label>Exp. Body Threshold <input type="number" class="ls-exp-body" value="${settings.exp_body ?? 0.5}" min="0" max="1" step="0.1"></label>
            <label style="display:flex;align-items:center;gap:6px;"><input type="checkbox" class="ls-show-levels" ${settings.show_levels ? 'checked' : ''}> Show Liquidity Levels</label>
            <div class="ism-section-title">EMA</div>
            <label>EMA Period <input type="number" class="ls-ema-len" value="${settings.ema_len ?? 20}" min="1" max="500"></label>
            ${ismColorField('EMA Color', 'ls-ema-color', settings.ema_color ?? '#00FF00')}
            <label>EMA Style
              <select class="ls-ema-style">
                <option value="0" ${settings.ema_style == null || settings.ema_style == 0 ? 'selected' : ''}>Solid</option>
                <option value="2" ${settings.ema_style == 2 ? 'selected' : ''}>Dashed</option>
                <option value="1" ${settings.ema_style == 1 ? 'selected' : ''}>Dotted</option>
              </select>
            </label>
            <label>EMA Width
              <select class="ls-ema-width">
                <option value="1" ${settings.ema_width == 1 ? 'selected' : ''}>1px</option>
                <option value="2" ${settings.ema_width == null || settings.ema_width == 2 ? 'selected' : ''}>2px</option>
                <option value="3" ${settings.ema_width == 3 ? 'selected' : ''}>3px</option>
                <option value="4" ${settings.ema_width == 4 ? 'selected' : ''}>4px</option>
              </select>
            </label>
            <div class="ism-section-title">EMA 200</div>
            <label>EMA 200 Period <input type="number" class="ls-ema200-len" value="${settings.ema200_len ?? 200}" min="1" max="500"></label>
            ${ismColorField('EMA 200 Color', 'ls-ema200-color', settings.ema200_color ?? '#6b7480')}
            <label>EMA 200 Style
              <select class="ls-ema200-style">
                <option value="0" ${settings.ema200_style == 0 ? 'selected' : ''}>Solid</option>
                <option value="2" ${settings.ema200_style == 2 ? 'selected' : ''}>Dashed</option>
                <option value="1" ${settings.ema200_style == null || settings.ema200_style == 1 ? 'selected' : ''}>Dotted</option>
              </select>
            </label>
            <label>EMA 200 Width
              <select class="ls-ema200-width">
                <option value="1" ${settings.ema200_width == 1 ? 'selected' : ''}>1px</option>
                <option value="2" ${settings.ema200_width == null || settings.ema200_width == 2 ? 'selected' : ''}>2px</option>
                <option value="3" ${settings.ema200_width == 3 ? 'selected' : ''}>3px</option>
                <option value="4" ${settings.ema200_width == 4 ? 'selected' : ''}>4px</option>
              </select>
            </label>
            <div class="ism-section-title">Liquidity Level Colors</div>
            ${ismColorField('Buy-Side Liquidity', 'ls-bsl-color', settings.bsl_color ?? '#ff4444')}
            ${ismColorField('Sell-Side Liquidity', 'ls-ssl-color', settings.ssl_color ?? '#44ff44')}
            <div class="ism-section-title">Pattern Marker Colors</div>
            ${ismColorField('Pattern 1 Down', 'ls-p1d-color', settings.p1d_color ?? '#ff0000')}
            ${ismColorField('Pattern 1 Up', 'ls-p1u-color', settings.p1u_color ?? '#00ff00')}
            ${ismColorField('Activation Down', 'ls-actd-color', settings.actd_color ?? '#800000')}
            ${ismColorField('Activation Up', 'ls-actu-color', settings.actu_color ?? '#00ff00')}
            ${ismColorField('Range Sweep', 'ls-rng-color', settings.rng_color ?? '#ffa500')}
            ${ismColorField('Expansion Up', 'ls-expu-color', settings.expu_color ?? '#00ff00')}
            ${ismColorField('Expansion Down', 'ls-expd-color', settings.expd_color ?? '#800000')}
          </div>
        `;
  }
  if (plugin === 'liquidity_sweep_new') {
    // Stripped 2026-08-20 to a display-only High-Volume Levels indicator: mark
    // each session's top-N highest-volume candles at their highs. All the old
    // fork machinery lives on in the liquidity_sweep baseline.
    return `
          <div class="ism-fields">
            <div class="ism-section-title">High-Volume Levels</div>
            <label>Number of Levels <input type="number" class="lsn-hivol-count" value="${settings.hivol_count ?? 4}" min="1" max="50"></label>
            ${ismColorField('Level Color', 'lsn-hivol-color', settings.hivol_color ?? '#ff0000')}
            <label>Line Style
              <select class="lsn-hivol-style">
                <option value="0" ${settings.hivol_style == null || settings.hivol_style == 0 ? 'selected' : ''}>Solid</option>
                <option value="2" ${settings.hivol_style == 2 ? 'selected' : ''}>Dashed</option>
                <option value="1" ${settings.hivol_style == 1 ? 'selected' : ''}>Dotted</option>
              </select>
            </label>
            <label>Line Width
              <select class="lsn-hivol-width">
                <option value="1" ${settings.hivol_width == null || settings.hivol_width == 1 ? 'selected' : ''}>1px</option>
                <option value="2" ${settings.hivol_width == 2 ? 'selected' : ''}>2px</option>
                <option value="3" ${settings.hivol_width == 3 ? 'selected' : ''}>3px</option>
                <option value="4" ${settings.hivol_width == 4 ? 'selected' : ''}>4px</option>
              </select>
            </label>
            <label>Recent Sessions <input type="number" class="lsn-levels-sessions" value="${settings.levels_max_sessions ?? 40}" min="1" max="1000"></label>
          </div>
        `;
  }
  if (plugin === 'oi_analysis') {
    return `
          <div class="ism-fields">
            <label style="display:flex;align-items:center;gap:6px;"><input type="checkbox" class="oi-auto-refresh" ${settings.auto_refresh !== false ? 'checked' : ''}> Auto-refresh (60s)</label>
            <div class="ism-section-title">Profile Colors</div>
            ${ismColorField('CE Color', 'oi-ce-color', settings.ce_color || '#2DBE8E')}
            ${ismColorField('PE Color', 'oi-pe-color', settings.pe_color || '#F06A6A')}
            <label>Opacity (0–1) <input type="number" class="oi-opacity" value="${settings.opacity != null ? settings.opacity : 0.4}" min="0" max="1" step="0.05"></label>
            <label>Max Bar Width (px) <input type="number" class="oi-max-bar-width" value="${settings.max_bar_width != null ? settings.max_bar_width : 120}" min="10" max="500" step="10"></label>
            <label>Bar Thickness (px) <input type="number" class="oi-bar-thickness" value="${settings.bar_thickness != null ? settings.bar_thickness : 16}" min="4" max="40" step="2"></label>
          </div>
        `;
  }
  if (plugin === 'vwap') {
    return `
          <div class="ism-fields">
            <label style="display:flex;align-items:center;gap:6px;"><input type="checkbox" class="vwap-show-bands" ${settings.show_bands !== false ? 'checked' : ''}> Show Standard Deviation Bands</label>
            <label>Band 1 Multiplier (σ) <input type="number" class="vwap-band1-mult" value="${settings.band1_multiplier ?? 1.0}" min="0.1" max="5" step="0.1"></label>
            <label>Band 2 Multiplier (σ) <input type="number" class="vwap-band2-mult" value="${settings.band2_multiplier ?? 2.0}" min="0.1" max="5" step="0.1"></label>
            ${ismColorField('VWAP Color', 'vwap-color', settings.vwap_color || '#E0A23B')}
            ${ismColorField('Band 1 Color', 'vwap-band1-color', settings.band1_color || '#9E9E9E')}
            ${ismColorField('Band 2 Color', 'vwap-band2-color', settings.band2_color || '#BDBDBD')}
            <label>Line Style
              <select class="vwap-line-style">
                <option value="line" ${(settings.line_style || 'line') === 'line' ? 'selected' : ''}>Line</option>
                <option value="cross" ${settings.line_style === 'cross' ? 'selected' : ''}>Cross</option>
                <option value="circles" ${settings.line_style === 'circles' ? 'selected' : ''}>Circles</option>
              </select>
            </label>
          </div>
        `;
  }
  if (plugin === 'panic_flush') {
    return `
          <div class="ism-fields">
            <label>Spike Multiplier <input type="number" class="pf-spike-mult" value="${settings.spike_multiplier ?? 2.0}" min="1" max="10" step="0.1"></label>
            <label>Lookback (bars) <input type="number" class="pf-lookback" value="${settings.lookback ?? 4}" min="1" max="50"></label>
            <label>Reversal Window (bars) <input type="number" class="pf-reversal-window" value="${settings.reversal_window ?? 3}" min="1" max="50"></label>
            <label>Skip Minutes (session open) <input type="number" class="pf-skip-minutes" value="${settings.skip_minutes ?? 45}" min="0" max="200"></label>
            <label>Min Body % of Price <input type="number" class="pf-min-body-pct" value="${((settings.min_body_pct ?? 0.001) * 100).toFixed(3)}" min="0" max="5" step="0.01"></label>
          </div>
        `;
  }
  if (plugin === 'psychological_view') {
    return `
          <div class="ism-fields">
            <label>Body % Threshold <input type="number" class="pv-body-pct" value="${Math.round((settings.body_pct ?? 0.70) * 100)}" min="1" max="100" step="1"></label>
            <label>Reversal % Threshold <input type="number" class="pv-rev-pct" value="${Math.round((settings.rev_pct ?? 0.30) * 100)}" min="1" max="100" step="1"></label>
            <label>Wick % Threshold <input type="number" class="pv-wick-pct" value="${Math.round((settings.wick_pct ?? 0.60) * 100)}" min="1" max="100" step="1"></label>
            <label style="display:flex;align-items:center;gap:6px;"><input type="checkbox" class="pv-show-sig" ${settings.show_sig !== false ? 'checked' : ''}> Show BUY/SELL Signals</label>
            <label style="display:flex;align-items:center;gap:6px;"><input type="checkbox" class="pv-show-avoid" ${settings.show_avoid !== false ? 'checked' : ''}> Show No Buy/No Sell Markers</label>
          </div>
        `;
  }
  if (plugin === 'liquidity_single_sweep') {
    return `
          <div class="ism-fields">
            <label>Swing Pivot Lookback <input type="number" class="lss-pivot-lb" value="${settings.liq_pivot_lb ?? 5}" min="1" max="20"></label>
            <label>Max Active Levels <input type="number" class="lss-max-levels" value="${settings.max_liq_levels ?? 6}" min="1" max="20"></label>
            <label>EMA Period <input type="number" class="lss-ema-len" value="${settings.ema_len ?? 20}" min="1" max="500"></label>
            <label>Activation Window <input type="number" class="lss-p1-act-win" value="${settings.p1_act_win ?? 30}" min="1" max="500"></label>
            <label style="display:flex;align-items:center;gap:6px;"><input type="checkbox" class="lss-show-levels" ${settings.show_levels ? 'checked' : ''}> Show Liquidity Levels</label>
            <div class="ism-section-title">Colors</div>
            ${ismColorField('EMA', 'lss-ema-color', settings.ema_color || '#b0b0b0')}
            ${ismColorField('Buy-Side Liquidity', 'lss-bsl-color', settings.bsl_color || '#ff4444')}
            ${ismColorField('Sell-Side Liquidity', 'lss-ssl-color', settings.ssl_color || '#44ff44')}
            ${ismColorField('Pattern 1 Down', 'lss-p1d-color', settings.p1d_color || '#ff0000')}
            ${ismColorField('Pattern 1 Up', 'lss-p1u-color', settings.p1u_color || '#00ff00')}
            ${ismColorField('Activation Down', 'lss-actd-color', settings.actd_color || '#800000')}
            ${ismColorField('Activation Up', 'lss-actu-color', settings.actu_color || '#00ff00')}
          </div>
        `;
  }
  return '<div class="ism-fields"><p style="font-size:12px;opacity:0.6;">No configurable settings for this indicator.</p></div>';
}
